import { useEmbedContext } from '@thoughtspot/blink-context';
import { getNonce } from '@thoughtspot/codec-util';
import {
    EventType,
    getEmbedLazyLoadingElementId,
    IncomingEventType,
} from '@thoughtspot/embed-util';
import { create } from '@thoughtspot/logger';
import React, { useCallback, useEffect, useRef } from 'react';
import styles from './visibleViewportEmbed.module.scss';

type VisibleEmbedViewportData = {
    top: number;
    left: number;
    width: number;
    height: number;
};

const logger = create('VisibleViewportEmbedContainer');
export const VisibleViewportEmbedContainer = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const { dispatchEmbedEvent, useEmbedEventSubscription } = useEmbedContext();

    const lazyLoadEmbedContainer = useRef<HTMLDivElement>(null);
    const lazyLoadEmbedContainerContent = useRef<HTMLDivElement>(null);

    const updateEmbedViewportElement = useCallback(
        (data: VisibleEmbedViewportData) => {
            if (lazyLoadEmbedContainer.current) {
                lazyLoadEmbedContainer.current.style.top = `${data.top}px`;
                lazyLoadEmbedContainer.current.style.left = `${data.left}px`;
                lazyLoadEmbedContainer.current.style.width = `${data.width}px`;
                lazyLoadEmbedContainer.current.style.height = `${data.height}px`;
            } else {
                logger.error('LazyLoadEmbedContainer not found');
            }
            if (lazyLoadEmbedContainerContent.current) {
                lazyLoadEmbedContainerContent.current.style.top = `-${data.top}px`;
                lazyLoadEmbedContainerContent.current.style.left = `-${data.left}px`;
            } else {
                logger.error('LazyLoadEmbedContainerContent not found');
            }
        },
        [],
    );

    useEffect(() => {
        dispatchEmbedEvent(EventType.RequestVisibleEmbedCoordinates, {})
            .then((data: VisibleEmbedViewportData): null => {
                if (data) updateEmbedViewportElement(data);
                return null;
            })
            .catch(e => {
                logger.error('Error requesting visible embed coordinates', e);
            });
    }, [updateEmbedViewportElement]);

    useEmbedEventSubscription(
        IncomingEventType.VisibleEmbedCoordinates,
        updateEmbedViewportElement,
    );

    const embedLazyLoadElementIds = getEmbedLazyLoadingElementId();

    return (
        <div
            id={embedLazyLoadElementIds.container}
            ref={lazyLoadEmbedContainer}
            className={styles.visibleViewportContainer}
            nonce={getNonce()}
        >
            <div
                id={`${embedLazyLoadElementIds.container}-content`}
                ref={lazyLoadEmbedContainerContent}
                className={styles.visibleViewportContainerContent}
                nonce={getNonce()}
            >
                {children}
            </div>
        </div>
    );
};
